import { ReactNode, useRef } from "react";
import {
  Button,
  Text,
  Card,
  Flex,
  Pill,
  Icon,
  Popover,
  Placement,
  type OpenCtrlRef
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { kebabCase } from "@/Utils/formatters";
import cx from "classnames";
import { enumFeaturesList, enumImageIndex } from "@coreapp/constants";
import {
  kImageFiles,
  gSuppressUpgradePopovers,
  gUpgradeIndicatorIcon
} from "@coreapp/whitelabel/brand";
import UpgradeButtons from "./UpgradeButtons";
import { DynamicTextProps } from "@theorgchartdev/storybook/dist/components/Text/DynamicText";

export type PopoverText = DynamicTextProps & {
  message: string;
};

type UpgradePopoverProps = {
  customTrigger?: ReactNode;
  pillLabel?: string;
  pillTestId?: string;
  popoverText?: PopoverText;
  featureKey: (typeof enumFeaturesList)[keyof typeof enumFeaturesList];
  placement?: Placement;
};

const UpgradePopover = ({
  customTrigger = null,
  pillLabel,
  pillTestId,
  popoverText,
  placement = "right",
  featureKey
}: UpgradePopoverProps) => {
  const openCtrlRef = useRef<OpenCtrlRef>(null);
  const resolvedPillTestId =
    pillTestId ?? (featureKey ? `${kebabCase(featureKey)}-upgrade-pill` : "upgrade-pill");
  const resolvedIconTestId =
    pillTestId != null
      ? `${pillTestId}-icon`
      : featureKey
        ? `${kebabCase(featureKey)}-upgrade-icon`
        : "upgrade-icon";

  const handleCloseButton = () => {
    if (openCtrlRef.current) {
      openCtrlRef.current.setOpen(false);
    }
  };

  const logoSrc = kImageFiles[enumImageIndex.logo];

  let pillLabelText = appstr("Upgrade to add more");

  if (pillLabel) {
    pillLabelText = pillLabel;
  }

  // Default pill element
  let pillElement = (
    <Pill
      className={cx("oc-relative", "oc-fit-content")}
      data-testid={resolvedPillTestId}
      label={pillLabelText}
      onClick={() => {}}
      color="brand"
      leftContent={
        <Icon
          name={gUpgradeIndicatorIcon}
          size="xsmall"
          color="inherit"
          dataTestId={resolvedIconTestId}
        />
      }
    />
  );

  let triggerElement = (
    <div
      style={{
        display: "inline-block",
        position: "relative",
        cursor: "pointer"
      }}
      onClick={() => {
        openCtrlRef.current?.setOpen(true);
      }}
    >
      {customTrigger ? customTrigger : pillElement}
    </div>
  );

  if (gSuppressUpgradePopovers) {
    return triggerElement;
  }

  return (
    <Popover
      reference={triggerElement}
      options={{ placement: placement }}
      openCtrlRef={openCtrlRef}
      arrowOptions={false}
    >
      <Card
        withShadow={true}
        style={{
          width: "360px",
          padding: "0px",
          borderRadius: "8px",
          zIndex: 9999,
          borderColor: "var(--border-brand-primary)"
        }}
      >
        <Flex
          direction="column"
          gap="12px"
          width="100%"
          style={{
            borderRadius: "4px",
            padding: "16px",
            boxSizing: "border-box"
          }}
        >
          <Flex
            alignItems={"center"}
            direction={"row"}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <img
              className="responsiveImage"
              src={logoSrc}
              alt="OrgChartLogo"
              style={{ width: "100px", height: "40px" }}
            />
            <Button
              iconOnly
              iconProps={{
                name: "xmark",
                size: "medium-small"
              }}
              modifier="tertiary"
              size="small"
              dataTestId={"upgrade-popover-close-btn"}
              onClick={handleCloseButton}
            />
          </Flex>
          <Flex gap={"12px"}>
            <Text
              size={"small"}
              type={"body"}
              dynamic={{
                formatConfig: popoverText?.formatConfig,
                replaceDict: popoverText?.replaceDict,
                template: popoverText?.template
              }}
            >
              {popoverText?.message}
            </Text>
          </Flex>
          <UpgradeButtons featureKey={featureKey} />
        </Flex>
      </Card>
    </Popover>
  );
};

export default UpgradePopover;
